'use client';

import NextImage from 'next/image';
import { type FC, useCallback } from 'react';
import { useSiteContext } from '@/context/SiteContext';
import { useSiteBaseUrl } from '@/utils/hooks/use-site-base-url';
import { isSvg, loader, type CloudinaryImageProps } from '@/utils/image-utils';

/**
 * This component wraps Next's image component to provide an image loader. An
 * image loader allows us to add query parameters for VIP's image processor.
 *
 * This uses a custom cached loader to be able to access the `aspectRatio` property. If
 * set, it will be used to calculate the height from the width so the image gets properly
 * cropped.
 *
 * Note that if this is a brand-new site instead of a migration it is best to just pass
 * the `loader` and remove the `internalLoader` fragment within this component to avoid
 * creating unnecessary callbacks per image.
 *
 * https://nextjs.org/docs/api-reference/next/image#loader
 * https://docs.wpvip.com/technical-references/vip-go-files-system/image-transformation/
 */
export const Image: FC<CloudinaryImageProps> = ({
	aspectRatio,
	focalX,
	focalY,
	...props
}) => {
	const siteBaseUrl = useSiteBaseUrl();
	const {
		config: { siteStatus },
	} = useSiteContext();

	const internalLoader = useCallback(
		(loaderProps: { quality?: number; src: string; width: number }) =>
			loader({
				...loaderProps,
				aspectRatio,
				focalX,
				focalY,
				siteBaseUrl,
				siteStatus,
			}),
		[aspectRatio, focalX, focalY, siteBaseUrl, siteStatus],
	);

	if (typeof props.src === 'string' && isSvg(props.src)) {
		// We want to make sure the SRC is correct for SVGs but we don't need to
		// generate the srcset for them.
		return (
			<NextImage
				{...props}
				src={loader({
					aspectRatio,
					focalX,
					focalY,
					siteBaseUrl,
					siteStatus,
					src: props.src,
					width: 0,
				})}
				unoptimized
			/>
		);
	}

	return <NextImage loader={internalLoader} {...props} />;
};
